import api from '@/global/api';
import qs from 'query-string';
import _get from 'lodash/get';
import _sumBy from "lodash/sumBy";
import _toLower from 'lodash/toLower';
import { getApiMiningType } from '@/shared/utils/apiMiningType';

export const DEFAULT_TRANSACTIONS_PAGE_SIZE = 30;

interface GetTransactionsPaginatedQueryArg {
  coin: string;
  account?: string;
  miner?: string;
  limit?: number;
  balanceId?: string | number;
  withBlockchainInfo?: boolean;
  orderBy?: string;
  isSortingDESC?: boolean;
}

interface TransactionsResponse<T> {
  transactions?: T[];
}

type TransactionDTO = Record<string, unknown>;

export const extendedApi = api.injectEndpoints({
  endpoints: (build) => ({
    getBalanceByCoin: build.query({
      keepUnusedDataFor: 60 * 60, // cache query for 1 hour
      query: (params = {}) => {
        const {
          account,
          miner,
        } = params;

        const { urlPartShort } = getApiMiningType({ miner, account });

        return {
          url: `/v1/${urlPartShort}/balances`,
          method: 'GET',
        }
      },
      transformResponse: (response: any, _meta, args) => {
        const { coin } = args;

        return _sumBy(_get(response, 'balances', []).filter((item: any) => item.currency === coin),
          ({ available = 0, locked = 0 }: { available: number | string, locked: number | string }) =>
            Number(available) + Number(locked)
        );
      }
    }),
    getBalanceId: build.query({
      keepUnusedDataFor: 60 * 60, // cache query for 1 hour
      query: (params = {}) => {
        const {
          account,
          miner,
        } = params;

        const { urlPartShort } = getApiMiningType({ miner, account });

        return {
          url: `/v1/${urlPartShort}/balances`,
          method: 'GET',
        }
      },
      transformResponse: (response: any, _meta, args) => {
        const { coin } = args;
        const balances = _get(response, 'balances', []).find((item: any) => {
          return item.type === 'TYPE_POOL_USER_REWARDS' && item.currency === coin
        }) || {};
        const id = _get(balances, 'id', null);
        return id;
      }
    }),
    getTransactions: build.query({
      query: (params = {}) => {
        const {
          coin,
          account,
          miner,
          offset,
          limit,
          balanceId,
          withBlockchainInfo,
          orderBy,
          isSortingDESC,
        } = params;

        const query = qs.stringify({
          'paginate.offset': offset,
          'paginate.limit': limit,
          'filter.balanceId': balanceId,
          'with.blockchainInfo': withBlockchainInfo,
          'orderBy.field': orderBy,
          'orderBy.desc': isSortingDESC,
        }, { skipEmptyString: true, skipNull: true });

        const coinToLower = _toLower(coin);
        const isBTC = coinToLower === 'btc';

        const { urlPartShort, urlPart } = getApiMiningType({ miner, account, coin: coinToLower });

        const btcUrl = `/v1/${urlPartShort}/transactions?${query}`;
        const withCoinUrl = `/v1/${urlPart}/transactions?${query}`;

        return {
          url: isBTC ? btcUrl : withCoinUrl,
          method: 'GET',
        }
      },
      transformResponse: (response: any) => {
        return _get(response, 'transactions', []);
      }
    }),
    getTransactionsPaginated: build.infiniteQuery<TransactionDTO[], GetTransactionsPaginatedQueryArg, number>({
      query: ({ queryArg, pageParam }) => {
        const {
          coin,
          account,
          miner,
          limit,
          balanceId,
          withBlockchainInfo,
          orderBy,
          isSortingDESC,
        } = queryArg;
        const paginationLimit = limit ?? DEFAULT_TRANSACTIONS_PAGE_SIZE;

        const query = qs.stringify({
          'paginate.offset': pageParam,
          'paginate.limit': paginationLimit,
          'filter.balanceId': balanceId,
          'with.blockchainInfo': withBlockchainInfo,
          'orderBy.field': orderBy,
          'orderBy.desc': isSortingDESC,
        }, { skipEmptyString: true, skipNull: true });

        const coinToLower = _toLower(coin);
        const isBTC = coinToLower === 'btc';

        const { urlPartShort, urlPart } = getApiMiningType({ miner, account, coin: coinToLower });

        const btcUrl = `/v1/${urlPartShort}/transactions?${query}`;
        const withCoinUrl = `/v1/${urlPart}/transactions?${query}`;

        return {
          url: isBTC ? btcUrl : withCoinUrl,
          method: 'GET',
        }
      },
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam, _allPageParams, queryArgs) => {
          const requestedLimit = queryArgs.limit ?? DEFAULT_TRANSACTIONS_PAGE_SIZE;

          if (lastPage.length < requestedLimit) {
            return undefined;
          }

          return lastPageParam + requestedLimit;
        },
      },
      transformResponse: (response: TransactionsResponse<TransactionDTO> | undefined) => {
        return response?.transactions ?? [];
      }
    }),
    getTransactionsShared: build.query({
      query: (params = {}) => {
        const {
          miner: userAddress,
          coin,
          offset,
          limit,
          type,
          sortBy,
        } = params;

        const query = qs.stringify({
          offset,
          limit,
          userAddress,
          coin,
          type,
          sortBy,
        }, { skipEmptyString: true, skipNull: true });

        return {
          url: `/v1/payouts/find?${query}`,
          method: 'GET',
        }
      },
    }),
    getTransactionsInfiniteShared: build.query({
      query: (params = {}) => {
        const {
          miner: userAddress,
          coin,
          offset,
          limit,
          type,
          sortBy,
        } = params;

        const query = qs.stringify({
          offset,
          limit,
          userAddress,
          coin,
          type,
          sortBy,
        }, { skipEmptyString: true, skipNull: true });

        return {
          url: `/v1/payouts/find?${query}`,
          method: 'GET',
        }
      },
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName
      },
      merge: (currentCache, newItems) => {
        // merge items
        currentCache?.items.push(...newItems.items);
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg
      },
    }),
    getRefundsShared: build.query({
      query: (params = {}) => {
        const {
          miner: minerAddress,
          type: txType = 'pool_refund',
          coin,
          offset,
          limit,
          sortBy,
        } = params;

        const query = qs.stringify({
          offset,
          limit,
          txType,
          sortBy,
        }, { skipEmptyString: true, skipNull: true });

        return {
          url: `/v1/stats/miner/${minerAddress}/${coin}/refunds?${query}`,
          method: 'GET',
        }
      },
    }),
  }),
});

export const {
  useGetBalanceIdQuery,
  useGetBalanceByCoinQuery,
  useGetTransactionsQuery,
  useGetTransactionsPaginatedInfiniteQuery,
  useGetTransactionsSharedQuery,
  useGetTransactionsInfiniteSharedQuery,
  useGetRefundsSharedQuery
} = extendedApi;

export const {
  getBalanceByCoin,
  getBalanceId,
  getTransactions,
  getTransactionsPaginated,
  getTransactionsShared,
  getTransactionsInfiniteShared,
  getRefundsShared,
} = extendedApi.endpoints;
